// src/Components/RouteForm.js
import { memo, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ReactSelect from 'react-select';
import api from '../api/api.js';
import { routeSchema } from '../lib/validations';
import Button from './ui/Button.jsx';
import InfoBox from './ui/InfoBox.jsx';
import { useToast } from './ui/Toast.jsx';
import f from '../styles/forms.module.css';

// ── Estilos inline para react-select — alineados al design system ──
const buildSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    height: '38px',
    minHeight: '38px',
    fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)',
    fontSize: '14px',
    color: 'var(--color-text-primary, #1A251B)',
    background: '#ffffff',
    border: hasError
      ? '0.5px solid var(--color-error, #C0392B)'
      : state.isFocused
        ? '0.5px solid var(--color-border-focus, #4CAF5C)'
        : '0.5px solid rgba(0, 0, 0, 0.25)',
    borderRadius: '8px',
    boxShadow: hasError
      ? '0 0 0 3px rgba(192, 57, 43, 0.10)'
      : state.isFocused
        ? '0 0 0 3px rgba(76, 175, 92, 0.15)'
        : 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    cursor: 'pointer',
    '&:hover': {
      borderColor: hasError ? 'var(--color-error)' : 'rgba(0, 0, 0, 0.4)',
    },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 10px' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 8px', color: '#7A8F7C' }),
  menu: (base) => ({
    ...base,
    borderRadius: '8px',
    border: '0.5px solid rgba(0,0,0,0.12)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
    zIndex: 9999,
    overflow: 'hidden',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    padding: '9px 12px',
    background: state.isSelected
      ? '#185FA5'
      : state.isFocused
        ? '#F0FAF0'
        : 'transparent',
    color: state.isSelected ? '#fff' : 'var(--color-text-primary, #1A251B)',
    cursor: 'pointer',
  }),
  placeholder: (base) => ({ ...base, color: '#7A8F7C', fontSize: '14px' }),
  singleValue: (base) => ({ ...base, color: 'var(--color-text-primary, #1A251B)' }),
});

const calcDuration = (dep, arr) => {
  if (!dep || !arr) return '';
  const [h1, m1] = dep.split(':').map(Number);
  const [h2, m2] = arr.split(':').map(Number);
  let diff = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (diff < 0) diff += 24 * 60;
  return (diff / 60).toFixed(1);
};

const RouteForm = memo(({ form, resetForm, terminalOptions, busOptions, onSuccess }) => {
  const isEditing = !!form.id;
  const { showToast } = useToast();
  const today = new Date().toLocaleDateString('en-CA');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(routeSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      date:                  form.date                  || '',
      departing_time:        form.departing_time        || '',
      arrival_time:          form.arrival_time          || '',
      departure_terminal_id: form.departure_terminal_id ? String(form.departure_terminal_id) : '',
      arrival_terminal_id:   form.arrival_terminal_id   ? String(form.arrival_terminal_id)   : '',
      distance:              form.distance              ? String(form.distance) : '',
      bus_id:                form.bus_id                ? String(form.bus_id)   : '',
      price:                 form.price                 ? String(form.price)    : '',
    },
  });

  useEffect(() => {
    reset({
      date:                  form.date                  || '',
      departing_time:        form.departing_time        || '',
      arrival_time:          form.arrival_time          || '',
      departure_terminal_id: form.departure_terminal_id ? String(form.departure_terminal_id) : '',
      arrival_terminal_id:   form.arrival_terminal_id   ? String(form.arrival_terminal_id)   : '',
      distance:              form.distance              ? String(form.distance) : '',
      bus_id:                form.bus_id                ? String(form.bus_id)   : '',
      price:                 form.price                 ? String(form.price)    : '',
    });
  }, [form.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [serverError, setServerError] = useState('');

  const departingTime       = watch('departing_time');
  const arrivalTime         = watch('arrival_time');
  const duration            = calcDuration(departingTime, arrivalTime);
  const departureTerminalId = watch('departure_terminal_id');

  const onSubmit = async (data) => {
    setServerError('');
    const departureName = terminalOptions.find(t => t.value === data.departure_terminal_id)?.label || '';
    const arrivalName   = terminalOptions.find(t => t.value === data.arrival_terminal_id)?.label   || '';

    const payload = {
      Origin:         departureName,
      Destination:    arrivalName,
      Duration:       Number(calcDuration(data.departing_time, data.arrival_time)),
      Distance:       Number(data.distance),
      Price:          Number(data.price),
      Departure_date: `${data.date} ${data.departing_time}:00`,
      Arrival_date:   `${data.date} ${data.arrival_time}:00`,
      ID_Bus:         Number(data.bus_id),
    };

    try {
      if (isEditing) {
        await api.put(`/route/${form.id}`, payload);
        onSuccess({ ID: form.id, ...payload });
      } else {
        const res = await api.post('/route', payload);
        onSuccess({
          ID:        res.data.routeId,
          ...payload,
          Bus_Plate: busOptions.find(b => b.value === data.bus_id)?.label?.split(' ')[0] || null,
        });
      }

      showToast(`Ruta ${isEditing ? 'actualizada' : 'creada'} correctamente`, { type: 'success' });
      resetForm();
    } catch (err) {
      setServerError(
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.error ||
        'Hubo un error al guardar la ruta.'
      );
    }
  };

  return (
    <div className={f.card}>
      {/* Header */}
      <div className={f.cardHeader}>
        <div className={f.cardTitleGroup}>
          <h3 className={f.cardTitle}>{isEditing ? 'Editar Ruta' : 'Nueva Ruta'}</h3>
          <p className={f.cardDesc}>
            {isEditing
              ? 'Modifica los parámetros de la ruta. La duración se calcula automáticamente.'
              : 'Define origen, destino, horario y bus para la nueva ruta.'}
          </p>
        </div>
        <span className={f.badgeAdmin}>Admin</span>
      </div>
      <hr className={f.divider} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {serverError && <div className={f.serverError} role="alert">{serverError}</div>}

        {/* ── Origen y Destino ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>Origen y Destino</span>
          <div className={f.grid2}>
            {/* Terminal origen */}
            <div className={f.field}>
              <label htmlFor="rt-dep-term" className={`${f.label} ${f.required}`}>Terminal Origen</label>
              <Controller
                name="departure_terminal_id"
                control={control}
                render={({ field }) => (
                  <ReactSelect
                    inputId="rt-dep-term"
                    options={terminalOptions}
                    value={terminalOptions.find(o => o.value === field.value) || null}
                    onChange={opt => field.onChange(opt?.value || '')}
                    onBlur={field.onBlur}
                    placeholder="Selecciona origen"
                    styles={buildSelectStyles(!!errors.departure_terminal_id)}
                    aria-invalid={!!errors.departure_terminal_id}
                    aria-describedby={errors.departure_terminal_id ? 'rt-dep-term-error' : undefined}
                  />
                )}
              />
              {errors.departure_terminal_id && (
                <span id="rt-dep-term-error" className={f.fieldError} role="alert">
                  {errors.departure_terminal_id.message}
                </span>
              )}
            </div>

            {/* Terminal destino */}
            <div className={f.field}>
              <label htmlFor="rt-arr-term" className={`${f.label} ${f.required}`}>Terminal Destino</label>
              <Controller
                name="arrival_terminal_id"
                control={control}
                render={({ field }) => (
                  <ReactSelect
                    inputId="rt-arr-term"
                    options={terminalOptions.filter(t => t.value !== departureTerminalId)}
                    value={terminalOptions.find(o => o.value === field.value) || null}
                    onChange={opt => field.onChange(opt?.value || '')}
                    onBlur={field.onBlur}
                    placeholder="Selecciona destino"
                    styles={buildSelectStyles(!!errors.arrival_terminal_id)}
                    aria-invalid={!!errors.arrival_terminal_id}
                    aria-describedby={errors.arrival_terminal_id ? 'rt-arr-term-error' : undefined}
                  />
                )}
              />
              {errors.arrival_terminal_id && (
                <span id="rt-arr-term-error" className={f.fieldError} role="alert">
                  {errors.arrival_terminal_id.message}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Horario ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>Horario</span>
          <div className={f.grid3}>
            {/* Fecha */}
            <div className={f.field}>
              <label htmlFor="rt-date" className={`${f.label} ${f.required}`}>Fecha</label>
              <input
                id="rt-date"
                type="date"
                min={today}
                className={`${f.input} ${errors.date ? f.inputError : ''}`}
                aria-describedby={errors.date ? 'rt-date-error' : undefined}
                aria-invalid={!!errors.date}
                {...register('date')}
              />
              {errors.date && <span id="rt-date-error" className={f.fieldError} role="alert">{errors.date.message}</span>}
            </div>

            {/* Salida */}
            <div className={f.field}>
              <label htmlFor="rt-dep-time" className={`${f.label} ${f.required}`}>Hora Salida</label>
              <input
                id="rt-dep-time"
                type="time"
                className={`${f.input} ${errors.departing_time ? f.inputError : ''}`}
                aria-describedby={errors.departing_time ? 'rt-dep-time-error' : undefined}
                aria-invalid={!!errors.departing_time}
                {...register('departing_time')}
              />
              {errors.departing_time && <span id="rt-dep-time-error" className={f.fieldError} role="alert">{errors.departing_time.message}</span>}
            </div>

            {/* Llegada */}
            <div className={f.field}>
              <label htmlFor="rt-arr-time" className={`${f.label} ${f.required}`}>Hora Llegada</label>
              <input
                id="rt-arr-time"
                type="time"
                className={`${f.input} ${errors.arrival_time ? f.inputError : ''}`}
                aria-describedby={errors.arrival_time ? 'rt-arr-time-error' : undefined}
                aria-invalid={!!errors.arrival_time}
                {...register('arrival_time')}
              />
              {errors.arrival_time && <span id="rt-arr-time-error" className={f.fieldError} role="alert">{errors.arrival_time.message}</span>}
            </div>
          </div>

          {/* Duración calculada */}
          {duration && (
            <InfoBox>
              Duración estimada: <strong>{duration} horas</strong> entre salida y llegada.
            </InfoBox>
          )}
        </div>

        {/* ── Detalles Operativos ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>Detalles Operativos</span>
          <div className={f.grid3}>
            {/* Bus */}
            <div className={f.field}>
              <label htmlFor="rt-bus" className={`${f.label} ${f.required}`}>Bus Asignado</label>
              <Controller
                name="bus_id"
                control={control}
                render={({ field }) => (
                  <ReactSelect
                    inputId="rt-bus"
                    options={busOptions}
                    value={busOptions.find(o => o.value === field.value) || null}
                    onChange={opt => field.onChange(opt?.value || '')}
                    onBlur={field.onBlur}
                    placeholder="Selecciona bus"
                    styles={buildSelectStyles(!!errors.bus_id)}
                    aria-invalid={!!errors.bus_id}
                    aria-describedby={errors.bus_id ? 'rt-bus-error' : undefined}
                  />
                )}
              />
              {errors.bus_id && <span id="rt-bus-error" className={f.fieldError} role="alert">{errors.bus_id.message}</span>}
            </div>

            {/* Distancia */}
            <div className={f.field}>
              <label htmlFor="rt-distance" className={`${f.label} ${f.required}`}>Distancia</label>
              <input
                id="rt-distance"
                type="number"
                step="0.1"
                min="0.1"
                placeholder="km"
                className={`${f.input} ${errors.distance ? f.inputError : ''}`}
                aria-describedby={errors.distance ? 'rt-distance-error' : 'rt-distance-hint'}
                aria-invalid={!!errors.distance}
                {...register('distance')}
              />
              {errors.distance
                ? <span id="rt-distance-error" className={f.fieldError} role="alert">{errors.distance.message}</span>
                : <span id="rt-distance-hint" className={f.fieldHint}>En kilómetros</span>
              }
            </div>

            {/* Precio */}
            <div className={f.field}>
              <label htmlFor="rt-price" className={`${f.label} ${f.required}`}>Precio</label>
              <input
                id="rt-price"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="$"
                className={`${f.input} ${errors.price ? f.inputError : ''}`}
                aria-describedby={errors.price ? 'rt-price-error' : 'rt-price-hint'}
                aria-invalid={!!errors.price}
                {...register('price')}
              />
              {errors.price
                ? <span id="rt-price-error" className={f.fieldError} role="alert">{errors.price.message}</span>
                : <span id="rt-price-hint" className={f.fieldHint}>Precio por pasaje</span>
              }
            </div>
          </div>
        </div>

        {/* ── Acciones ── */}
        <div className={f.actions}>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {isEditing ? 'Actualizar Ruta' : 'Crear Ruta'}
          </Button>
          {isEditing && (
            <Button type="button" variant="secondary" onClick={resetForm}>
              Cancelar
            </Button>
          )}
        </div>
      </form>
    </div>
  );
});

export default RouteForm;
